import React from 'react';
import PropTypes from 'prop-types';
import { Table, Button } from 'reactstrap';
import { TransferItemRow } from './TransferItemRow';
import { getProductIdsWithTransactions } from '../../../helpers/transferStatusCalculator';

class TransferItemsTable extends React.Component {
    rowRefs = {};

    _renderTransferButton(position) {
        const { selectedItems, onTransfer, isTransferring } = this.props;
        const hasSelection = selectedItems.size > 0;

        return (
            <div className={`d-flex justify-content-between mb-2 ${position === 'bottom' ? 'mt-2' : ''}`}>
                <Button
                    color="primary"
                    disabled={!hasSelection || isTransferring}
                    onClick={onTransfer}
                >
                    {isTransferring ? 'Transferring...' : 'Transfer Inventory'}
                </Button>
                {hasSelection && (
                    <span className="text-muted align-self-center">
                        {selectedItems.size} item(s) selected
                    </span>
                )}
            </div>
        );
    }

    _getAllDisplayItems() {
        const { items, transactions } = this.props;

        // Get all product IDs that have transactions
        const productIdsWithTransactions = getProductIdsWithTransactions(transactions);

        // Current items from PO
        const currentItemIds = new Set(items.map(item => item.product.id));

        // Find removed items (have transactions but not in current PO)
        const removedItems = productIdsWithTransactions
            .filter(productId => !currentItemIds.has(productId))
            .map(productId => {
                // Build a minimal item from transaction history
                const itemTransactions = transactions.filter(tx => tx.productId === productId);
                const lastTx = itemTransactions[itemTransactions.length - 1];
                return {
                    product: {
                        id: productId,
                        name: (lastTx && lastTx.productName) || `Product ${productId}`,
                        piShopSku: lastTx && lastTx.sku
                    },
                    quantity: (lastTx && lastTx.poQty) || 0,
                    _isRemoved: true
                };
            });

        // Combine current items and removed items
        // Note: explicitly copy quantity and receivingTotals since they're getters on PurchaseOrderItem class
        return [
            ...items.map(item => ({
                ...item,
                quantity: item.quantity,
                receivingTotals: item.receivingTotals,
                _isRemoved: false
            })),
            ...removedItems
        ];
    }

    resetDangerModes = () => {
        Object.values(this.rowRefs).forEach(ref => {
            if (ref && ref.resetDangerMode) {
                ref.resetDangerMode();
            }
        });
    };

    getManualDeltas = () => {
        const deltas = {};
        Object.entries(this.rowRefs).forEach(([productId, ref]) => {
            if (ref && ref._getManualDelta) {
                const delta = ref._getManualDelta();
                if (delta !== null) {
                    deltas[productId] = delta;
                }
            }
        });
        return deltas;
    };

    render() {
        const {
            transactions,
            selectedItems,
            transferDirections,
            onSelectChange,
            onDirectionChange,
            productsClient
        } = this.props;

        const displayItems = this._getAllDisplayItems();

        return (
            <div>
                {this._renderTransferButton('top')}

                <Table striped responsive>
                    <thead>
                        <tr>
                            <th style={{ width: '5%' }} className="text-center">Select</th>
                            <th style={{ width: '8%' }}>KID</th>
                            <th style={{ width: '10%' }}>PS SKU</th>
                            <th style={{ width: '25%' }}>Product Name</th>
                            <th style={{ width: '6%' }} className="text-center">Qty</th>
                            <th style={{ width: '12%' }} className="text-center">Receiving</th>
                            <th style={{ width: '10%' }} className="text-center">Historic Delta</th>
                            <th style={{ width: '12%' }}>Direction</th>
                            <th style={{ width: '12%' }}>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {displayItems.map(item => (
                            <TransferItemRow
                                key={item.product.id}
                                ref={ref => { this.rowRefs[item.product.id] = ref; }}
                                item={item}
                                transactions={transactions}
                                isSelected={selectedItems.has(item.product.id)}
                                transferDirection={transferDirections.get(item.product.id) || 'toMarketplace'}
                                isRemoved={item._isRemoved}
                                onSelectChange={onSelectChange}
                                onDirectionChange={onDirectionChange}
                                productsClient={productsClient}
                            />
                        ))}
                    </tbody>
                </Table>

                {this._renderTransferButton('bottom')}
            </div>
        );
    }
}

TransferItemsTable.propTypes = {
    items: PropTypes.array.isRequired,
    transactions: PropTypes.array.isRequired,
    selectedItems: PropTypes.instanceOf(Set).isRequired,
    transferDirections: PropTypes.instanceOf(Map).isRequired,
    onSelectChange: PropTypes.func.isRequired,
    onDirectionChange: PropTypes.func.isRequired,
    onTransfer: PropTypes.func.isRequired,
    isTransferring: PropTypes.bool,
    productsClient: PropTypes.object.isRequired
};

TransferItemsTable.defaultProps = {
    isTransferring: false
};

export { TransferItemsTable };
